import { action, makeObservable, observable } from "mobx"

import type { IEventBus } from "@/domains/events"

import type { SwapAggregatorDAO } from "./types"
import { SwapAggregatorDisabledEvent, SwapAggregatorEnabledEvent } from "../swap/events"

const LOCAL_STORAGE_PREFIX = "swap-aggregator-enabled-"

export class SwapAggregatorStore {
  // Static data
  public readonly id: string
  public readonly name: string
  public readonly description: string
  public readonly icon: string
  public readonly logoUrl?: string
  public readonly isProductionReady: boolean

  // Dynamic state
  public enabled: boolean

  constructor(
    data: SwapAggregatorDAO,
    private readonly eventBus?: IEventBus,
  ) {
    this.id = data.id
    this.name = data.name
    this.description = data.description
    this.icon = data.icon
    this.logoUrl = data.logoUrl
    this.isProductionReady = data.isProductionReady
    this.enabled = data.isActive

    makeObservable(this, {
      enabled: observable,
      setEnabled: action,
    })

    // Load from localStorage after mobx setup
    this.loadEnabledState()
  }

  private getLocalStorageKey(): string {
    return `${LOCAL_STORAGE_PREFIX}${this.id}`
  }

  private loadEnabledState(): void {
    const savedEnabled = localStorage.getItem(this.getLocalStorageKey())
    if (savedEnabled !== null) {
      this.enabled = savedEnabled === "true"
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled
    localStorage.setItem(this.getLocalStorageKey(), enabled.toString())

    // Emit domain events when enabled state changes
    if (this.eventBus) {
      if (enabled) {
        this.eventBus.publish(new SwapAggregatorEnabledEvent(this.id))
      } else {
        this.eventBus.publish(new SwapAggregatorDisabledEvent(this.id))
      }
    }
  }

  toggleEnabled(): void {
    this.setEnabled(!this.enabled)
  }
}
